import React from 'react';
import type { ScenarioKind, ScreenVariant } from './home-content';

type Building = {
  x: number;
  y: number;
  w: number;
  dep: number;
  bh: number;
  roof: string;
};

const BUILDINGS: Building[] = [
  { x: 760, y: 470, w: 26, dep: 14, bh: 24, roof: 'var(--roof-c)' },
  { x: 900, y: 430, w: 30, dep: 16, bh: 34, roof: 'var(--roof-b)' },
  { x: 1040, y: 400, w: 22, dep: 12, bh: 42, roof: 'var(--roof-a)' },
  { x: 1180, y: 450, w: 34, dep: 18, bh: 26, roof: 'var(--roof-d)' },
  { x: 1320, y: 420, w: 24, dep: 14, bh: 30, roof: 'var(--roof-b)' },
  { x: 820, y: 540, w: 36, dep: 18, bh: 22, roof: 'var(--roof-a)' },
  { x: 990, y: 560, w: 28, dep: 16, bh: 36, roof: 'var(--roof-c)' },
  { x: 1140, y: 530, w: 40, dep: 20, bh: 28, roof: 'var(--roof-b)' },
  { x: 1300, y: 560, w: 26, dep: 14, bh: 40, roof: 'var(--roof-d)' },
];

const TREES_BACK: Array<[number, number, number]> = [
  [700, 500, 0.85],
  [860, 470, 0.7],
  [1100, 490, 0.9],
  [1420, 500, 0.8],
];

const TREES_FRONT: Array<[number, number, number]> = [
  [620, 600, 0.95],
  [760, 630, 0.75],
  [1080, 620, 1],
  [1240, 640, 0.8],
  [240, 650, 0.9],
  [380, 680, 0.7],
];

const PINS: Array<{ x: number; y: number; delay: string }> = [
  { x: 690, y: 500, delay: '0s' },
  { x: 860, y: 560, delay: '0.4s' },
  { x: 1080, y: 470, delay: '0.8s' },
  { x: 1260, y: 540, delay: '1.1s' },
];

function Cloud({ x, y, s = 1 }: { x: number; y: number; s?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`} fill="var(--cloud)">
      <ellipse cx="0" cy="8" rx="34" ry="16" />
      <ellipse cx="-24" cy="12" rx="20" ry="13" />
      <ellipse cx="22" cy="10" rx="24" ry="14" />
    </g>
  );
}

function Tree({ x, y, s = 1 }: { x: number; y: number; s?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <rect x="-3" y="6" width="6" height="16" rx="2" fill="var(--tree-dark)" />
      <circle cx="0" cy="-2" r="16" fill="var(--tree)" />
      <circle cx="-11" cy="6" r="11" fill="var(--tree)" />
      <circle cx="12" cy="6" r="10" fill="var(--tree-dark)" />
    </g>
  );
}

function IsoBuilding({ x, y, w, dep, bh, roof }: Building) {
  const n = [0, 0];
  const e = [w, dep / 2];
  const s = [0, dep];
  const west = [-w, dep / 2];
  const up = (point: number[]) => `${point[0]},${point[1] - bh}`;
  return (
    <g transform={`translate(${x} ${y})`}>
      <polygon points={`${west.join(',')} ${s.join(',')} ${up(s)} ${up(west)}`} fill="var(--wall-shade)" />
      <polygon points={`${e.join(',')} ${s.join(',')} ${up(s)} ${up(e)}`} fill="var(--wall)" />
      <polygon points={`${up(n)} ${up(e)} ${up(s)} ${up(west)}`} fill={roof} />
    </g>
  );
}

function Pin({ x, y, delay }: { x: number; y: number; delay: string }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <g className="ml-pin" style={{ animationDelay: delay }}>
        <path
          fill="var(--pin)"
          d="M0 0c-7 0-12 5.4-12 13.2C-12 24 0 38 0 38s12-14 12-24.8C12 5.4 7 0 0 0z"
        />
        <circle cy="13" r="4.5" fill="#fff" />
      </g>
    </g>
  );
}

function HeroSceneSvg() {
  const buildings = [...BUILDINGS].sort((a, b) => a.y - b.y);
  return (
    <svg viewBox="0 0 1440 760" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="1440" height="760" fill="var(--sky-bottom)" />
      <ellipse cx="720" cy="40" rx="980" ry="420" fill="var(--sky-top)" />
      <g className="ml-scene__sun">
        <circle cx="1220" cy="120" r="78" fill="#ffe08a" opacity="0.35" />
        <circle cx="1220" cy="120" r="42" fill="#ffe08a" />
      </g>
      <g className="ml-scene__moon">
        <circle cx="1220" cy="120" r="36" fill="#f4f1ff" />
        <circle cx="1208" cy="110" r="7" fill="#e3def4" />
        <circle cx="1232" cy="132" r="4" fill="#e3def4" />
      </g>
      <g className="ml-scene__star" fill="#fff">
        <circle cx="180" cy="80" r="1.6" />
        <circle cx="260" cy="140" r="1.3" />
        <circle cx="420" cy="70" r="1.8" />
        <circle cx="540" cy="130" r="1.2" />
        <circle cx="160" cy="190" r="1.4" />
        <circle cx="680" cy="90" r="1.5" />
      </g>
      <Cloud x={180} y={120} />
      <Cloud x={420} y={80} s={0.8} />
      <Cloud x={860} y={150} s={1.1} />
      <path
        fill="var(--hill-back)"
        d="M0 430C240 350 480 470 760 390c280-70 480 20 680-30v400H0V430z"
      />
      <path
        fill="var(--hill-mid)"
        d="M0 520C220 460 440 580 720 500c280-80 460 30 720-20v280H0V520z"
      />
      <path
        fill="var(--hill-front)"
        d="M0 620C280 560 560 680 900 600c220-50 380 10 540-20v180H0V620z"
      />
      <ellipse cx="520" cy="650" rx="78" ry="24" fill="var(--water)" />
      <ellipse cx="500" cy="644" rx="28" ry="8" fill="#fff" opacity="0.35" />
      <path
        d="M-20 690C220 640 380 720 560 660s320-80 520-20 360-40 420 10"
        fill="none"
        stroke="var(--path)"
        strokeWidth="18"
        strokeLinecap="round"
      />
      <path
        d="M80 600C260 520 460 590 680 520s300-90 520 0"
        fill="none"
        stroke="var(--route)"
        strokeWidth="6"
        strokeLinecap="round"
      />
      {TREES_BACK.map(([x, y, s]) => (
        <Tree key={`b-${x}-${y}`} x={x} y={y} s={s} />
      ))}
      {buildings.map((building) => (
        <IsoBuilding key={`${building.x}-${building.y}`} {...building} />
      ))}
      {TREES_FRONT.map(([x, y, s]) => (
        <Tree key={`f-${x}-${y}`} x={x} y={y} s={s} />
      ))}
      <ellipse cx="250" cy="680" rx="16" ry="8" fill="var(--tree)" />
      <ellipse cx="470" cy="700" rx="18" ry="9" fill="var(--tree-dark)" />
      <g transform="translate(930 600)">
        <circle r="18" fill="var(--route)" opacity="0.2" />
        <circle r="7" fill="var(--route)" stroke="#fff" strokeWidth="3" />
      </g>
      {PINS.map((pin) => (
        <Pin key={`${pin.x}-${pin.y}`} x={pin.x} y={pin.y} delay={pin.delay} />
      ))}
    </svg>
  );
}

type HeroSceneProps = {
  image?: string;
  label: string;
};

export const HeroScene: React.FC<HeroSceneProps> = ({ image, label }) => (
  <div
    className="ml-hero__scene"
    data-art-slot="hero-scene"
    role={image ? undefined : 'img'}
    aria-label={image ? undefined : label}
  >
    {image ? <img src={image} alt={label} /> : <HeroSceneSvg />}
  </div>
);

const MAP_LINES: Partial<Record<ScreenVariant, string[]>> = {
  hero: ['Stationary pointer', 'Stationary playlist', 'Moving route'],
  point: ['Fixed point', 'Saved place', 'Start mock'],
  playlist: ['Home', 'Cafe stop', 'Park'],
  route: ['Start', 'Waypoint', 'Finish'],
};

export const ScreenArt: React.FC<{ variant: ScreenVariant }> = ({ variant }) => {
  if (variant === 'schedule') {
    return (
      <div className="ml-screen ml-screen--schedule" aria-hidden="true">
        <span className="ml-screen__time">9:41</span>
        <p className="ml-screen__top">Scheduler</p>
        <ul className="ml-screen__list">
          <li><b>08:00</b><span>Office</span></li>
          <li><b>12:30</b><span>Cafe</span></li>
          <li><b>18:00</b><span>Home</span></li>
        </ul>
      </div>
    );
  }

  if (variant === 'settings') {
    return (
      <div className="ml-screen ml-screen--settings" aria-hidden="true">
        <span className="ml-screen__time">9:41</span>
        <p className="ml-screen__top">Route settings</p>
        <div className="ml-screen__list">
          {['Speed', 'Accuracy', 'Repeat'].map((row) => (
            <div key={row} className="ml-screen__toggle">
              <span>{row}</span>
              <i />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'more') {
    return (
      <div className="ml-screen ml-screen--more" aria-hidden="true">
        <span className="ml-screen__time">9:41</span>
        <p className="ml-screen__top">More options</p>
        <div className="ml-screen__tiles">
          {Array.from({ length: 6 }, (_, index) => (
            <i key={index} />
          ))}
        </div>
      </div>
    );
  }

  const lines = MAP_LINES[variant] ?? MAP_LINES.hero ?? [];
  return (
    <div className={`ml-screen ml-screen--${variant}`} aria-hidden="true">
      <span className="ml-screen__time">9:41</span>
      <div className="ml-screen__map">
        <span className="ml-screen__blob ml-screen__blob--a" />
        <span className="ml-screen__blob ml-screen__blob--b" />
        <span className="ml-screen__route" />
        <span className="ml-screen__dot ml-screen__dot--a" />
        <span className="ml-screen__dot ml-screen__dot--b" />
      </div>
      {variant === 'joystick' ? (
        <div className="ml-screen__stick" />
      ) : (
        <div className="ml-screen__sheet">
          {lines.map((line) => (
            <p key={line}>
              <i />
              <span>{line}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

type DeviceFrameProps = {
  slot: string;
  label: string;
  variant: ScreenVariant;
  image?: string;
  className?: string;
};

export const DeviceFrame: React.FC<DeviceFrameProps> = ({
  slot,
  label,
  variant,
  image,
  className,
}) => (
  <div className={`ml-device${className ? ` ${className}` : ''}`} data-art-slot={slot}>
    <div className="ml-device__screen">
      {image ? <img src={image} alt={label} /> : <ScreenArt variant={variant} />}
      {!image && <span className="ml-sr">{label}</span>}
      <span className="ml-device__notch" aria-hidden="true" />
    </div>
  </div>
);

function TestingArt() {
  return (
    <svg viewBox="0 0 640 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="640" height="400" fill="#d7ebff" />
      <circle cx="90" cy="70" r="28" fill="#fff" opacity="0.8" />
      <rect x="70" y="230" width="500" height="130" rx="28" fill="#f7fbff" />
      <rect x="150" y="96" width="280" height="176" rx="16" fill="#243049" />
      <rect x="166" y="112" width="248" height="128" rx="8" fill="#b7e4ff" />
      <circle cx="250" cy="176" r="26" fill="#8ed0a4" />
      <path d="M200 200c40-50 90-10 150-40" fill="none" stroke="#3b82f6" strokeWidth="6" strokeLinecap="round" />
      <circle cx="300" cy="168" r="8" fill="#ff4d6a" />
      <rect x="470" y="150" width="28" height="18" rx="6" fill="#9fd6ff" />
      <rect x="458" y="166" width="52" height="64" rx="10" fill="#fff" />
      <circle cx="560" cy="250" r="26" fill="#b7e7c2" />
      <rect x="554" y="250" width="12" height="46" rx="4" fill="#6fbf86" />
    </svg>
  );
}

function GamingArt() {
  return (
    <svg viewBox="0 0 640 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="640" height="400" fill="#efe7ff" />
      <circle cx="540" cy="70" r="40" fill="#fff" opacity="0.45" />
      <rect x="110" y="48" width="420" height="230" rx="26" fill="#2a2152" />
      <rect x="132" y="70" width="376" height="168" rx="14" fill="#9fd0ff" />
      <circle cx="230" cy="150" r="28" fill="#86d7a4" />
      <path d="M180 180c50-60 120-20 200-50" fill="none" stroke="#3b82f6" strokeWidth="7" strokeLinecap="round" />
      <circle cx="360" cy="140" r="9" fill="#ff4d6a" />
      <rect x="150" y="230" width="340" height="100" rx="50" fill="#6d5cff" />
      <circle cx="230" cy="280" r="26" fill="#241a4a" />
      <circle cx="400" cy="280" r="26" fill="#241a4a" />
      <circle cx="230" cy="280" r="10" fill="#d9d4ff" />
      <circle cx="400" cy="280" r="10" fill="#ff8fbe" />
    </svg>
  );
}

function TravelArt() {
  return (
    <svg viewBox="0 0 640 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="640" height="400" fill="#d7ecff" />
      <circle cx="500" cy="80" r="36" fill="#ffe08a" />
      <path d="M0 250l140-90 90 60 120-100 110 70 180-80v290H0V250z" fill="#8eb7e8" />
      <path d="M0 300l120-50 100 40 140-70 90 40 190-60v200H0V300z" fill="#6f9ad4" />
      <ellipse cx="320" cy="360" rx="280" ry="36" fill="#d9f5d4" />
      <path d="M80 350c80-20 140 10 220-10s150-20 260 10" fill="none" stroke="#f0d089" strokeWidth="10" strokeLinecap="round" />
      <path fill="#ff4d6a" d="M300 188c-16 0-28 12-28 30 0 24 28 52 28 52s28-28 28-52c0-18-12-30-28-30z" />
      <circle cx="300" cy="216" r="8" fill="#fff" />
    </svg>
  );
}

function PrivacyArt() {
  return (
    <svg viewBox="0 0 640 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="640" height="400" fill="#e5f8f6" />
      <circle cx="120" cy="80" r="50" fill="#fff" opacity="0.7" />
      <circle cx="520" cy="300" r="70" fill="#c8f3ee" />
      <path
        fill="#3b82f6"
        d="M320 70l120 42v96c0 70-120 130-120 130S200 278 200 208V112L320 70z"
      />
      <path
        fill="#fff"
        d="M320 108l78 28v62c0 46-78 86-78 86s-78-40-78-86v-62l78-28z"
      />
      <rect x="302" y="176" width="36" height="30" rx="6" fill="#3b82f6" />
      <path d="M310 176v-12a10 10 0 0 1 20 0v12" fill="none" stroke="#3b82f6" strokeWidth="6" />
    </svg>
  );
}

const SCENARIO_ART: Record<ScenarioKind, React.FC> = {
  testing: TestingArt,
  gaming: GamingArt,
  travel: TravelArt,
  privacy: PrivacyArt,
};

type ScenarioArtProps = {
  kind: ScenarioKind;
  slot: string;
  label: string;
  image?: string;
};

export const ScenarioArt: React.FC<ScenarioArtProps> = ({ kind, slot, label, image }) => {
  const Art = SCENARIO_ART[kind];
  return (
    <div className="ml-scenario__art" data-art-slot={slot} role="img" aria-label={label}>
      {image ? <img src={image} alt="" /> : <Art />}
    </div>
  );
};
